/**
 * SD-2526 Stage B: assembles a streamed assistant message from `chatStream` `delta` frames keyed
 * by their monotonic `seq`, tolerant of the out-of-order + duplicate frames a reconnect replay
 * produces. It renders only the contiguous prefix (seq `0..highestContiguousSeq`): a delta beyond a
 * gap is buffered until the gap fills, so text never renders out of order, and applying the same
 * seq twice is idempotent. Framework-free so the ordering logic is unit-testable in isolation.
 */
export class StreamDeltaBuffer {
  private readonly deltas = new Map<number, string>();
  private contiguousText = '';
  private contiguousSeq = -1;

  /** The contiguous streamed text so far (seq `0..highestContiguousSeq`). */
  get text(): string {
    return this.contiguousText;
  }

  /**
   * Highest seq with no gap below it — the `lastSeq` a reconnecting client asks the BE to replay
   * past. `-1` when nothing contiguous has arrived yet.
   */
  get highestContiguousSeq(): number {
    return this.contiguousSeq;
  }

  /**
   * Record a delta at its seq and return the (possibly unchanged) contiguous text. A seq at or
   * below the contiguous high-water mark is a duplicate/replay and is ignored; a seq beyond a gap
   * is buffered until the gap fills, at which point the contiguous run advances in one step.
   */
  apply(seq: number, delta: string): string {
    if (seq <= this.contiguousSeq) {
      return this.contiguousText;
    }
    this.deltas.set(seq, delta);
    let next = this.contiguousSeq + 1;
    while (this.deltas.has(next)) {
      this.contiguousText += this.deltas.get(next) ?? '';
      this.contiguousSeq = next;
      next++;
    }
    return this.contiguousText;
  }

  /**
   * SD-2716: a new LLM round is about to stream. Allo's agent loop re-runs the model after
   * every tool round and each round rewrites the WHOLE answer, but only the last round is
   * persisted (`AlloGraphBuilder.lastAssistantText`). Drop the superseded text while KEEPING
   * the seq high-water mark — `seq` is per-turn on the BE (`StreamingDeltaEmitter.seq` never
   * restarts mid-turn), so it must keep driving duplicate suppression and the reconnect
   * `lastSeq`.
   */
  startRound(): void {
    this.contiguousText = '';
  }

  /** Clear all state for a new turn / chat switch. */
  reset(): void {
    this.deltas.clear();
    this.contiguousText = '';
    this.contiguousSeq = -1;
  }
}
